import React, { useState, useEffect, useCallback } from "react";
import {
    Drawer, Box, Typography, IconButton, Chip, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Alert, Tooltip,
} from "@mui/material";
import { Close as CloseIcon, SwapHoriz as TransferIcon, Tune as AdjustIcon, AccountBalance, Refresh as RefreshIcon } from "@mui/icons-material";
import { financialAccountService } from "../../services/financialAccountService";
import { FinancialAccountWithBalance, AccountStatementEntry } from "../../types/accounts";
import { formatCurrencyBDT } from "../../utils/currency";
import { TransferDialog } from "./TransferDialog";
import { AdjustmentDialog } from "./AdjustmentDialog";
import { ACCOUNT_TYPE_META } from "./accountTypeMeta";
import { usePermissions } from "../../hooks/usePermissions";

interface AccountStatementDrawerProps {
    open: boolean;
    accountId: string | null;
    onClose: () => void;
}

const SOURCE_LABELS: Record<string, string> = {
    order_payment: "Order Payment",
    expense: "Expense",
    purchase_payment: "Supplier Payment",
    asset_purchase: "Asset Purchase",
    transfer_in: "Transfer In",
    transfer_out: "Transfer Out",
    adjustment: "Adjustment",
};

export const AccountStatementDrawer: React.FC<AccountStatementDrawerProps> = ({ open, accountId, onClose }) => {
    const { isOwner } = usePermissions();
    const [account, setAccount] = useState<FinancialAccountWithBalance | null>(null);
    const [entries, setEntries] = useState<AccountStatementEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [transferOpen, setTransferOpen] = useState(false);
    const [adjustOpen, setAdjustOpen] = useState(false);

    const load = useCallback(async () => {
        if (!accountId) return;
        setLoading(true);
        setError("");
        try {
            const [acct, statement] = await Promise.all([
                financialAccountService.getAccount(accountId),
                financialAccountService.getStatement(accountId),
            ]);
            setAccount(acct);
            setEntries(statement);
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to load account statement");
        } finally {
            setLoading(false);
        }
    }, [accountId]);

    useEffect(() => {
        if (open && accountId) load();
    }, [open, accountId, load]);

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: { xs: "100%", sm: 600, md: 700 },
                        maxWidth: "100%",
                        mt: "64px",
                        height: "calc(100% - 64px)",
                    },
                }}
                sx={{ zIndex: 1200 }}
            >
                <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                                {account && (
                                    <Box sx={{
                                        bgcolor: ACCOUNT_TYPE_META[account.type].avatarBg,
                                        color: ACCOUNT_TYPE_META[account.type].avatarFg,
                                        p: 0.75, borderRadius: 1.5, display: "flex", flexShrink: 0,
                                    }}>
                                        {ACCOUNT_TYPE_META[account.type].icon}
                                    </Box>
                                )}
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="h6" noWrap>{account?.name || "…"}</Typography>
                                    {account && (
                                        <Chip
                                            label={ACCOUNT_TYPE_META[account.type].label}
                                            color={ACCOUNT_TYPE_META[account.type].chipColor}
                                            size="small" variant="outlined" sx={{ mt: 0.5 }}
                                        />
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                                <Tooltip title="Refresh">
                                    <span>
                                        <IconButton onClick={load} disabled={loading}>
                                            <RefreshIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <IconButton onClick={onClose}><CloseIcon /></IconButton>
                            </Box>
                        </Box>
                        {account && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {account.type === "LOAN" ? "Amount owed" : "Current balance"}
                                </Typography>
                                <Typography variant="h4" color={account.type === "LOAN" ? "error.main" : "success.main"}>
                                    {formatCurrencyBDT(account.balance)}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                            <Button
                                startIcon={<TransferIcon />}
                                variant="outlined"
                                size="small"
                                onClick={() => setTransferOpen(true)}
                            >
                                Transfer
                            </Button>
                            {isOwner && (
                                <Button
                                    startIcon={<AdjustIcon />}
                                    variant="outlined"
                                    color="warning"
                                    size="small"
                                    onClick={() => setAdjustOpen(true)}
                                >
                                    Adjust Balance
                                </Button>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ flexGrow: 1, overflow: "auto", p: 3 }}>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : entries.length === 0 ? (
                            <Box sx={{ textAlign: "center", py: 4 }}>
                                <Box sx={{ display: "inline-flex", bgcolor: "grey.100", color: "grey.500", p: 1.25, borderRadius: "50%", mb: 1 }}>
                                    <AccountBalance />
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    No activity tagged to this account yet.
                                </Typography>
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Description</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {entries.map((e) => (
                                            <TableRow key={`${e.source_type}-${e.reference_id}`}>
                                                <TableCell>{new Date(e.date).toLocaleDateString()}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{e.description}</Typography>
                                                    <Chip
                                                        label={SOURCE_LABELS[e.source_type] || e.source_type}
                                                        size="small" variant="outlined"
                                                        sx={{ mt: 0.25, height: 18, fontSize: "0.65rem", "& .MuiChip-label": { px: 0.75 } }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                        color={e.amount >= 0 ? "success.main" : "error.main"}
                                                    >
                                                        {e.amount >= 0 ? "+" : ""}
                                                        {formatCurrencyBDT(e.amount)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Box>
            </Drawer>

            <TransferDialog
                open={transferOpen}
                onClose={() => setTransferOpen(false)}
                defaultFromAccountId={accountId || undefined}
                onSuccess={() => {
                    setTransferOpen(false);
                    load();
                }}
            />

            <AdjustmentDialog
                open={adjustOpen}
                onClose={() => setAdjustOpen(false)}
                account={account}
                onSuccess={() => {
                    setAdjustOpen(false);
                    load();
                }}
            />
        </>
    );
};
